import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [error, setError] = useState(null)

  // Enhanced profile operations with better error handling and automatic profile creation
  const profileOperations = {
    async load(userId, userEmail) {
      if (!userId) return
      setProfileLoading(true)
      setError(null)
      
      try {
        // First try to get existing profile with case-insensitive email match
        let { data: profile, error: profileError } = await supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('id', userId)
          ?.single()

        if (profileError) {
          console.warn('Profile lookup error:', profileError)
          
          // If profile doesn't exist, try to create it
          if (profileError?.code === 'PGRST116') { // No rows returned
            console.log('Profile not found, attempting to create for user:', userEmail)
            
            const { data: createdProfile, error: createError } = await supabase
              ?.from('user_profiles')
              ?.insert({
                id: userId,
                email: userEmail?.toLowerCase(),
                full_name: userEmail?.split('@')?.[0] || 'User',
                role: 'member'
              })
              ?.select()
              ?.single()

            if (createError) {
              console.error('Failed to create profile:', createError)
              setError(`Profile creation failed: ${createError?.message}`)
              return
            } else {
              profile = createdProfile
              console.log('Profile created successfully:', profile)
            }
          } else {
            // Other database errors
            console.error('Database error during profile load:', profileError)
            setError(`Profile load failed: ${profileError?.message}`)
            return
          }
        }

        // Check if user account is active
        if (profile && !profile?.is_active) {
          setError('Your account has been deactivated. Please contact support.')
          return
        }

        setUserProfile(profile)
        console.log('Profile loaded successfully for:', userEmail)
        
      } catch (error) {
        console.error('Unexpected error during profile load:', error)
        setError(`Unexpected error: ${error?.message}`)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
      setError(null)
    },

    async sync(userId, userEmail) {
      // Sync profile if user exists but profile is missing or outdated
      try {
        const { data: authUser } = await supabase?.auth?.getUser()
        
        if (authUser?.user && !userProfile) {
          console.log('Syncing profile for authenticated user:', userEmail)
          await this.load(userId, userEmail)
        }
      } catch (error) {
        console.error('Profile sync error:', error)
      }
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      console.log('Auth state change:', event, session?.user?.email)
      setUser(session?.user ?? null)
      setLoading(false)
      setError(null)
      
      if (session?.user) {
        // Fire-and-forget profile loading with email for debugging
        profileOperations?.load(session?.user?.id, session?.user?.email)
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check with enhanced error handling
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase?.auth?.getSession()
        
        if (error) {
          console.error('Session initialization error:', error)
          setError(`Authentication error: ${error?.message}`)
          setLoading(false)
          return
        }
        
        authStateHandlers?.onChange('INITIAL_SESSION', session)
      } catch (error) {
        console.error('Auth initialization failed:', error)
        setError(`Failed to initialize authentication: ${error?.message}`)
        setLoading(false)
      }
    }

    initializeAuth()

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Enhanced auth methods with better error handling
  const signIn = async (email, password) => {
    try {
      setError(null)
      console.log('Attempting sign in for:', email?.toLowerCase())
      
      const { data, error } = await supabase?.auth?.signInWithPassword({ 
        email: email?.toLowerCase()?.trim(), 
        password 
      })
      
      if (error) {
        console.error('Sign in error:', error)
        return { data: null, error }
      }
      
      console.log('Sign in successful for:', email?.toLowerCase())
      return { data, error: null }
      
    } catch (error) {
      console.error('Network error during sign in:', error)
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      setError(null)
      const { error } = await supabase?.auth?.signOut()
      
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      
      return { error }
    } catch (error) {
      console.error('Sign out error:', error)
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      setError(null)
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update(updates)
        ?.eq('id', user?.id)
        ?.select()
        ?.single()
        
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      console.error('Profile update error:', error)
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // Debug function to manually retry profile loading
  const retryProfileLoad = async () => {
    if (user && !userProfile && !profileLoading) {
      console.log('Manual profile retry for:', user?.email)
      await profileOperations?.load(user?.id, user?.email)
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    error, // Expose error for debugging
    signIn,
    signOut,
    updateProfile,
    retryProfileLoad, // Expose retry function
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}